import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Grid,
  TextField,
  Button,
  Link,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  IconButton,
  Checkbox,
  FormControlLabel,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useForm, SubmitHandler } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { loginUser, clearError } from "src/features/authSlice";
import { RootState, AppDispatch } from "src/store";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useNavigate } from "react-router-dom";
import { AccountType } from "src/types/firebase";
import { useTranslation } from "react-i18next";

interface LoginFormInputs {
  email: string;
  password: string;
  accountType: AccountType;
}

const LoginPage: React.FC = () => {
  const { t } = useTranslation();

  // Memoize schema - only recreate when language changes
  const schema = useMemo(
    () => Yup.object().shape({
      email: Yup.string()
        .email(t("auth.validation.invalidEmail"))
        .required(t("auth.validation.email")),
      password: Yup.string()
        .required(t("auth.validation.password"))
        .min(6, t("auth.validation.passwordMinLength", { count: 6 })),
      accountType: Yup.string()
        .oneOf(["talent", "company", "backoffice"])
        .required(t("auth.validation.accountType")),
    }),
    [t]
  );
  const resolver = useMemo(() => yupResolver(schema), [schema]);

  const [accountType, setAccountType] = useState<AccountType>("company");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);

  const handleClickShowPassword = () => setShowPassword((prev) => !prev);

  // Load saved email on mount
  useEffect(() => {
    const savedEmail = localStorage.getItem("rememberedEmail");
    if (savedEmail) {
      setRememberMe(true);
    }
  }, []);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormInputs>({
    resolver: resolver as any,
    defaultValues: {
      accountType: "company",
      email: localStorage.getItem("rememberedEmail") || "",
    },
  });

  const dispatch = useDispatch<AppDispatch>();
  const { loading, error, isAuthenticated } = useSelector(
    (state: RootState) => state.auth
  );
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated) {
      navigate("/", { replace: true });
    }
  }, [isAuthenticated, navigate]);

  useEffect(() => {
    // Clear error when component unmounts or account type changes
    return () => {
      dispatch(clearError());
    };
  }, [dispatch, accountType]);

  const onSubmit: SubmitHandler<LoginFormInputs> = async (data) => {
    dispatch(clearError());

    // Handle remember me
    if (rememberMe) {
      localStorage.setItem("rememberedEmail", data.email);
    } else {
      localStorage.removeItem("rememberedEmail");
    }

    await dispatch(loginUser(data));
  };

  return (
    <Grid container sx={{ minHeight: "100vh", p: [2, 4] }}>
      <Grid
        item
        xs={12}
        md={6}
        sx={{
          bgcolor: "primary.main",
          alignItems: "center",
          justifyContent: "center",
          color: "white",
          borderRadius: 5,
          boxShadow: 3,
          display: { xs: "none", md: "flex" },
          flexDirection: "column",
          pb: [0, 28],
        }}
      >
        <Box>
          <img
            src="/logo.png"
            alt="Logo"
            style={{ height: 230, filter: "invert(1)" }}
          />
        </Box>
        <Typography variant="h4" align="center">
          {t("auth.login.welcomeBack")}
        </Typography>
      </Grid>
      <Grid
        item
        xs={12}
        md={6}
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          padding: "20px",
        }}
      >
        <Box sx={{ maxWidth: 400, mx: "auto", width: "100%" }}>
          <Box
            sx={{
              display: { xs: "flex", md: "none" },
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <img src="/logo.png" alt="Logo" style={{ height: 180 }} />
          </Box>
          <Typography variant="h5" gutterBottom>
            {t("auth.login.title")}
          </Typography>
          {error && (
            <Typography color="error" variant="body2" gutterBottom sx={{ mb: 2 }}>
              {error}
            </Typography>
          )}
          <form onSubmit={handleSubmit(onSubmit)}>
            <TextField
              label={t("auth.login.email")}
              fullWidth
              margin="normal"
              {...register("email")}
              error={!!errors.email}
              helperText={errors.email?.message}
            />
            <TextField
              label={t("auth.login.password")}
              type={showPassword ? "text" : "password"}
              fullWidth
              margin="normal"
              {...register("password")}
              error={!!errors.password}
              helperText={errors.password?.message}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassword}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  color="primary"
                />
              }
              label={t("auth.login.rememberMe")}
              sx={{ mt: 1 }}
            />
            {/* Account type selection - commented out, defaulting to company */}
            {/* <FormControl fullWidth margin="normal" error={!!errors.accountType}>
              <InputLabel id="account-type-label">{t("auth.forgotPassword.accountType")}</InputLabel>
              <Select
                labelId="account-type-label"
                label={t("auth.forgotPassword.accountType")}
                value={accountType}
                {...register("accountType")}
                onChange={(e) => setAccountType(e.target.value as AccountType)}
              >
                <MenuItem value="talent">{t("auth.common.talent")}</MenuItem>
                <MenuItem value="company">{t("auth.common.company")}</MenuItem>
                <MenuItem value="backoffice">{t("auth.common.backoffice")}</MenuItem>
              </Select>
              {errors.accountType && (
                <Typography variant="caption" color="error">
                  {errors.accountType.message}
                </Typography>
              )}
            </FormControl> */}
            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              disabled={loading}
              sx={{ mt: 2, fontWeight: "bold" }}
            >
              {loading ? t("auth.login.loggingIn") : t("auth.login.loginButton")}
            </Button>
          </form>
          <Box mt={2}>
            <Typography variant="body2" fontWeight={"bold"}>
              <Link href="/#/forgot-password" underline="none">
                {t("auth.login.forgotPassword")}
              </Link>
            </Typography>
          </Box>
          <Box mt={2}>
            <Typography variant="body2">
              {t("auth.login.notRegistered")}{" "}
              <Link href="/#/signup" underline="none">
                {t("auth.login.createAccount")}
              </Link>
            </Typography>
          </Box>
        </Box>
      </Grid>
    </Grid>
  );
};

export default LoginPage;
